import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, FolderOpen, MessageCircle, X } from "lucide-react";
import { Overline, Reveal } from "./Reveal";
import { api } from "../lib/api";
import { waSitesLink, WA_MESSAGES } from "../config/site";
import { trackEvent } from "../lib/analytics";

const ProjectModal = ({ project, onClose }) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[80] flex items-center justify-center overflow-y-auto bg-black/80 p-4 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`Projeto ${project.client_name}`}
      data-testid="project-modal"
    >
      <motion.div
        initial={{ opacity: 0, y: 34, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 34, scale: 0.96 }}
        transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
        className="card-chrome relative my-auto w-full max-w-2xl overflow-hidden rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Fechar projeto"
          data-testid="project-modal-close"
          className="absolute right-4 top-4 z-10 rounded-full border border-white/15 bg-gb-black/70 p-2.5 text-gb-gray backdrop-blur transition-colors hover:text-gb-white"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>

        {project.image_url && (
          <img
            src={project.image_url}
            alt={`Site do projeto ${project.client_name}`}
            width={1280}
            height={720}
            className="aspect-video w-full object-cover"
          />
        )}

        <div className="p-6 sm:p-8">
          <div className="flex flex-wrap items-center gap-2 font-mono text-[10px] uppercase tracking-[0.18em] text-gb-silver">
            <span className="rounded-full border border-white/10 px-3 py-1">{project.segment}</span>
            <span className="rounded-full border border-white/10 px-3 py-1">{project.service_type}</span>
            {project.example && (
              <span className="rounded-full border border-gb-silver/35 bg-gb-silver/10 px-3 py-1 text-gb-silverhi">
                Exemplo ilustrativo
              </span>
            )}
          </div>

          <h3 className="mt-5 font-display text-2xl font-bold">{project.client_name}</h3>

          {project.challenge && (
            <div className="mt-6">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gb-silver">O desafio</p>
              <p className="mt-2 text-sm leading-relaxed text-gb-gray sm:text-base">{project.challenge}</p>
            </div>
          )}
          {project.solution && (
            <div className="mt-5">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gb-silver">A solução GOBAX</p>
              <p className="mt-2 text-sm leading-relaxed text-gb-gray sm:text-base">{project.solution}</p>
            </div>
          )}
          {project.results && (
            <div className="mt-5 rounded-xl border border-gb-silver/20 bg-gb-silver/5 p-4">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gb-silver">Resultados</p>
              <p className="mt-2 text-sm font-semibold text-gb-silverhi">{project.results}</p>
            </div>
          )}

          <a
            href={waSitesLink(WA_MESSAGES.sites)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackEvent("whatsapp_click", { location: `project_${project.client_name}` })}
            data-testid="project-modal-cta"
            className="btn-chrome mt-8 inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold"
          >
            <MessageCircle className="h-4 w-4" aria-hidden="true" />
            Quero um projeto assim
            <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
          </a>
        </div>
      </motion.div>
    </motion.div>
  );
};

export const Portfolio = () => {
  const [projects, setProjects] = useState(null);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    api
      .get("/projects")
      .then((r) => setProjects(r.data))
      .catch(() => setProjects([]));
  }, []);

  const openProject = projects?.find((p) => p.id === openId) || null;

  return (
    <section id="portfolio" data-testid="portfolio-section" className="relative border-y border-white/5 bg-gb-ink/40 py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
        <Reveal>
          <Overline>Portfólio</Overline>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-6 max-w-2xl font-display text-2xl font-bold leading-tight tracking-tight sm:text-3xl lg:text-4xl">
            Projetos com <span className="chrome-text">estratégia por trás</span>
          </h2>
        </Reveal>
        <Reveal delay={0.16}>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-gb-gray">
            Clique em um projeto para ver o desafio e a solução por dentro. Trabalhos reais são
            publicados aqui com autorização do cliente.
          </p>
        </Reveal>

        {!projects || projects.length === 0 ? (
          <Reveal delay={0.2}>
            <div
              data-testid="portfolio-empty-state"
              className="mt-16 flex flex-col items-center rounded-2xl border border-dashed border-gb-silver/25 bg-white/[0.015] px-8 py-20 text-center"
            >
              <span className="flex h-14 w-14 items-center justify-center rounded-full border border-gb-silver/25 bg-white/[0.03]">
                <FolderOpen className="h-6 w-6 text-gb-silver" aria-hidden="true" />
              </span>
              <h3 className="mt-6 font-display text-xl font-bold">Estrutura pronta para grandes projetos</h3>
              <p className="mt-3 max-w-md text-sm leading-relaxed text-gb-gray">
                Este espaço foi preparado para receber trabalhos reais da GOBAX. Novos projetos são
                publicados aqui assim que entregues e autorizados.
              </p>
            </div>
          </Reveal>
        ) : (
          <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {projects.map((p, i) => (
              <Reveal key={p.id} delay={0.08 * i}>
                <article
                  data-testid={`portfolio-card-${p.id}`}
                  className="card-glass group h-full cursor-pointer overflow-hidden"
                  role="button"
                  tabIndex={0}
                  aria-label={`Abrir projeto ${p.client_name}`}
                  onClick={() => setOpenId(p.id)}
                  onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setOpenId(p.id)}
                >
                  {p.image_url ? (
                    <div className="relative overflow-hidden">
                      <img src={p.image_url} alt={`Projeto ${p.client_name}`} width={640} height={400} loading="lazy" className="aspect-[8/5] w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                      <span className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-gb-black/75 px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.18em] text-gb-silverhi opacity-0 backdrop-blur-sm transition-opacity duration-300 group-hover:opacity-100">
                        Ver projeto
                        <ArrowUpRight className="h-3 w-3" aria-hidden="true" />
                      </span>
                    </div>
                  ) : (
                    <div className="grid-bg flex aspect-[8/5] w-full items-center justify-center bg-gb-black">
                      <img src="/assets/logo.jpg" alt="Logotipo GOBAX" width={120} height={120} loading="lazy" className="h-24 w-24 rounded-full opacity-80 ring-1 ring-white/15" />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex flex-wrap items-center gap-2 font-mono text-[10px] uppercase tracking-[0.18em] text-gb-silver">
                      <span className="rounded-full border border-white/10 px-3 py-1">{p.segment}</span>
                      <span className="rounded-full border border-white/10 px-3 py-1">{p.service_type}</span>
                      {p.example && (
                        <span data-testid={`portfolio-example-badge-${p.id}`} className="rounded-full border border-gb-silver/35 bg-gb-silver/10 px-3 py-1 text-gb-silverhi">
                          Exemplo ilustrativo
                        </span>
                      )}
                    </div>
                    <h3 className="card-glass-title mt-4 font-display text-lg font-bold">{p.client_name}</h3>
                    {p.solution && <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-gb-gray">{p.solution}</p>}
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        )}
      </div>

      <AnimatePresence>
        {openProject && <ProjectModal project={openProject} onClose={() => setOpenId(null)} />}
      </AnimatePresence>
    </section>
  );
};
